/**
 * @fileoverview The first-load "enable location" dialog.
 * @description Shown by LocationGate over the app when location isn't on as the app first loads.
 * The browser's own permission popup is opened at the same moment, by location.store.ts — never
 * from this dialog's button. Enable only re-checks: until location is really on it does nothing,
 * and once it is, the dialog closes for the rest of this load. There's deliberately no close
 * button, Escape or backdrop dismiss. Copy varies by reason: null (not answered yet) asks the
 * visitor to allow it in the browser popup, "denied" points at the browser/device settings, and
 * every other reason (unavailable/timeout/unsupported) shares a "turn on your device's location"
 * message.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'

import locationIcon from '@/assets/svg/errors/icon-location.svg'
import Button from '@/shared/components/atoms/Button'

import type { GeoBlockReason } from '@/shared/lib/device/geolocation.utils'

interface LocationPromptDialogProps {
  reason: GeoBlockReason | null
  isConfirming: boolean
  onEnable: () => void
}

/**
 * @description Picks the copy for the current reason: "intro" while the visitor hasn't answered,
 * "denied" for a blocked permission, "unavailable" for every other failure.
 * @param {GeoBlockReason | null} reason - Why there's no location yet, or null if not answered.
 * @returns {'intro' | 'denied' | 'unavailable'} The copy key segment to translate.
 */
function resolveCopyKey(reason: GeoBlockReason | null): 'intro' | 'denied' | 'unavailable' {
  if (reason === null) {
    return 'intro'
  }
  return reason === 'denied' ? 'denied' : 'unavailable'
}

/**
 * @component
 * @param {LocationPromptDialogProps} props - The current reason and the Enable handler.
 * @param {GeoBlockReason | null} props.reason - Why location isn't available, or null if the
 * visitor hasn't answered the browser's popup yet.
 * @param {boolean} props.isConfirming - Shows Enable as busy while it re-checks.
 * @param {() => void} props.onEnable - Called when Enable is clicked.
 */
function LocationPromptDialog({ reason, isConfirming, onEnable }: LocationPromptDialogProps) {
  const { t } = useTranslation()
  const copyKey = resolveCopyKey(reason)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-ink/55 px-4 py-10">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="location-prompt-title"
        aria-describedby="location-prompt-description"
        className="w-full max-w-[420px] rounded-[18px] bg-white p-6 text-center shadow-[0_24px_60px_rgba(11,26,52,0.32)] sm:p-8"
      >
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <img src={locationIcon} alt="" className="h-7 w-7" />
        </div>
        <h2
          id="location-prompt-title"
          className="mb-2.5 text-[22px] font-extrabold leading-[1.3] text-ink"
        >
          {t(`locationGate.${copyKey}.title`)}
        </h2>
        <p
          id="location-prompt-description"
          className="mx-auto mb-6 max-w-[320px] text-[14px] leading-[22px] text-slate"
        >
          {t(`locationGate.${copyKey}.description`)}
        </p>
        <Button type="button" size="md" isLoading={isConfirming} onClick={onEnable}>
          {t('locationGate.enable')}
        </Button>
      </div>
    </div>
  )
}

export default LocationPromptDialog
